import React from 'react';
import { Platform, View, Image, StyleProp, ViewStyle, StyleSheet } from 'react-native';
import { colors, shadows } from '../../../utils/theme';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import Text, { TextColor, TextVariant } from '../Text';
import Stack, { StackProps, contentStackSpacing } from '../Stack';
import { useAvatar } from '../../../hooks/useAvatar';
import urls from '../../../utils/urls';
import Chip, { ChipProps, ChipSet } from '../Chip';
import SkeletonLoadingProvider, { useIsSkeletonByParent } from '../SkeletonLoadingProvider';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import ReferenceCounts from '../ReferenceCounts';
import { IconAvatar, IconAvatarProps, LoadableAvatar, LoadableAvatarProps } from '../Avatar';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { useIsInSideTabContentContainer } from '../TabView';

export const CardReferenceCounts = ReferenceCounts;

export type CardProps = {
    onPress?: () => void;
    children: React.ReactNode;
    isDisabled?: boolean;
    testID?: string;
    isSkeletonLoading?: boolean;
    disablePadding?: boolean;
    style?: StyleProp<ViewStyle>;
    flat?: boolean;
};

export const defaultCardPadding = {
    paddingTop: 18,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 18,
};

export const cardTopContentPadding: StyleProp<ViewStyle> = {
    paddingTop: defaultCardPadding.paddingTop,
    paddingLeft: defaultCardPadding.paddingLeft,
    paddingRight: defaultCardPadding.paddingRight,
};

export const cardBottomContentPadding: StyleProp<ViewStyle> = {
    paddingBottom: defaultCardPadding.paddingBottom,
    paddingLeft: defaultCardPadding.paddingLeft,
    paddingRight: defaultCardPadding.paddingRight,
};

export const cardContentPadding: StyleProp<ViewStyle> = {
    ...cardTopContentPadding,
    ...cardBottomContentPadding,
};

const styles = StyleSheet.create({
    cardBase: {
        backgroundColor: colors.White,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: colors['Grey-Light2'],
        width: '100%',
    },
    cardBaseWithPadding: {
        ...defaultCardPadding,
    },
    cardBaseWithShadow: {
        ...shadows.card,
    },
    cardBaseInSideTab: {
        borderWidth: 1,
        borderColor: colors['Paper-Border'],
    },
    cardPadding: {
        width: '100%',
        ...cardContentPadding,
    },
    cardSkeleton: {
        width: '100%',
        height: 80,
    },
    cardMinimalContent: {
        borderBottomWidth: 1,
        borderBottomColor: colors['Dividers'],
    },
    cardInlineContent: {
        alignSelf: 'stretch',
    },
    cardRightContent: {
        flex: 1,
        height: '100%',
    },
    cardLeftContent: {
        flex: 1,
        height: '100%',
    },
    cardImageContainer: {
        flex: 1,
    },
    cardImage: {
        resizeMode: 'contain',
    },
});

export const Card: React.FC<CardProps> = ({
    children,
    onPress,
    isDisabled,
    testID,
    isSkeletonLoading,
    disablePadding,
    style,
    flat,
}) => {
    const isInSideTabContentContainer = useIsInSideTabContentContainer();
    const maybeSkeletonByParent = useIsSkeletonByParent(isSkeletonLoading);
    const maybeParentDisabled = useIsDisabledByParent(isDisabled);
    const cardBase = (
        <SkeletonLoadingProvider isSkeletonLoading={maybeSkeletonByParent}>
            <View
                testID={testID}
                style={[
                    styles.cardBase,
                    !disablePadding && styles.cardBaseWithPadding,
                    isInSideTabContentContainer || flat ? styles.cardBaseInSideTab : styles.cardBaseWithShadow,
                    style,
                ]}
            >
                {children}
            </View>
        </SkeletonLoadingProvider>
    );

    return onPress ? (
        <PressableDynamicOpacity
            onPress={onPress}
            isDisabled={maybeParentDisabled}
            children={cardBase}
            style={{ width: '100%' }}
        />
    ) : (
        cardBase
    );
};

export const CardPadding: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <View style={styles.cardPadding}>{children}</View>
);

export const CardSkeleton: React.FC = () => {
    return (
        <Card isSkeletonLoading>
            <View style={styles.cardSkeleton} />
        </Card>
    );
};

export type CardTextProps = {
    children: string | undefined | null;
};

type CardTextBaseProps = {
    variant: TextVariant;
    color: TextColor;
} & CardTextProps;

const CardTextBase: React.FC<CardTextBaseProps> = ({ children, variant, color }) => {
    const isSkeletonLoading = useIsSkeletonByParent();
    if (!children && !isSkeletonLoading) {
        return null;
    }

    return (
        <SkeletonLoadingContainer isLoading={isSkeletonLoading}>
            <Text variant={variant} color={color} wrapText>
                {children || 'skeleton loading text placeholder'}
            </Text>
        </SkeletonLoadingContainer>
    );
};

export type CardMinimalProps = {
    style?: StyleProp<ViewStyle>;
    children: React.ReactNode;
    onPress?: () => void;
    isDisabled?: boolean;
};

export const CardMinimal: React.FC<CardMinimalProps> = ({ style, children, onPress, isDisabled }) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const content = <CardContent style={[styles.cardMinimalContent, style]}>{children}</CardContent>;

    return onPress ? (
        <PressableDynamicOpacity isDisabled={isDisabledByParent} onPress={onPress}>
            {content}
        </PressableDynamicOpacity>
    ) : (
        content
    );
};

export const CardHeader: React.FC<CardTextProps> = ({ children }) => {
    return (
        <CardTextBase variant="h4" color="Dark-Text">
            {children}
        </CardTextBase>
    );
};

export const CardListHeader: React.FC<CardTextProps> = ({ children }) => {
    return (
        <CardTextBase variant="text-button-text" color="Dark-Text">
            {children}
        </CardTextBase>
    );
};

export const CardBody: React.FC<CardTextProps & { color?: TextColor }> = ({ children, color }) => {
    return (
        <CardTextBase variant="body-1" color={color || 'Dark-Text'}>
            {children}
        </CardTextBase>
    );
};

export const CardBody2: React.FC<CardTextProps & { color?: TextColor }> = ({ children, color }) => {
    return (
        <CardTextBase variant="body-2" color={color || 'Gray-Text'}>
            {children}
        </CardTextBase>
    );
};

export const CardCaption: React.FC<CardTextProps> = ({ children }) => {
    return (
        <CardTextBase variant="caption-1" color="Gray-Text">
            {children}
        </CardTextBase>
    );
};

export const CardCaptionError: React.FC<CardTextProps> = ({ children }) => {
    return (
        <CardTextBase variant="caption-1" color="Alert">
            {children}
        </CardTextBase>
    );
};

export type CardContentProps = {
    children: StackProps['children'];
    style?: StyleProp<ViewStyle>;
};

export const CardContent: React.FC<CardContentProps> = ({ children, style }) => {
    return (
        <Stack vertical space={2} style={style}>
            {children}
        </Stack>
    );
};

export type CardInlineContentProps = {
    children: StackProps['children'];
};

export const CardInlineContent: React.FC<CardInlineContentProps> = ({ children }) => {
    return (
        <Stack horizontal fullWidth justify="flex-start" align="center" space={2.5} style={styles.cardInlineContent}>
            {children}
        </Stack>
    );
};

export type VerticalSplitCardContentProps = {
    children: React.ReactNode;
};
export const VerticalSplitCardContent: React.FC<VerticalSplitCardContentProps> = ({ children }) => {
    return (
        <Stack vertical fullWidth justify="space-between" space={contentStackSpacing} style={{ alignSelf: 'stretch' }}>
            <>{children}</>
        </Stack>
    );
};

export type CardRightContentProps = {
    rightContent: React.ReactNode;
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

export const CardRightContent: React.FC<CardRightContentProps> = ({ rightContent, children, style }) => {
    return (
        <Stack horizontal fullWidth justify="space-between" space={2.5} style={[styles.cardInlineContent, style]}>
            <View style={styles.cardRightContent}>{children}</View>
            <>{rightContent}</>
        </Stack>
    );
};

export type CardLeftContentProps = {
    leftContent: React.ReactNode;
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

export const CardLeftContent: React.FC<CardLeftContentProps> = ({ leftContent, children, style }) => {
    return (
        <Stack horizontal fullWidth justify="space-between" space={2.5} style={style}>
            {leftContent}
            <View style={styles.cardLeftContent}>{children}</View>
        </Stack>
    );
};

export type CardImageContainer = {
    children: React.ReactNode;
};
export const CardImageContainer: React.FC<CardImageContainer> = ({ children }) => {
    return (
        <Stack vertical justify="center" fullHeight>
            <>{children}</>
        </Stack>
    );
};

export type CardChipProps = ChipProps;
export const CardChip = Chip;

export const CardChipSet = ChipSet;

export type CardImageProps = {
    alt: string;
    url: string;
    size: { width: number; height: number };
};

export const CardImage: React.FC<CardImageProps> = ({ alt, url, size }) => {
    const image = useAvatar({
        avatarAPIUrl: Platform.select({
            // On the web, we hit a CORS issue, so we have to go through a proxy
            web: urls.api.ehrIntegrations.oneUpHealth.endpoints.image(url),
            default: url,
        }),
    });

    return (
        <SkeletonLoadingContainer isLoading={image.isLoadingAvatar}>
            <View style={[{ width: size.width, height: size.height }]}>
                {image.isLoadingAvatar ? null : (
                    <Image
                        style={{ width: size.width, height: size.height }}
                        source={{
                            uri: image?.avatar?.base64,
                        }}
                        alt={alt}
                    />
                )}
            </View>
        </SkeletonLoadingContainer>
    );
};

export const CardLoadableAvatar: React.FC<LoadableAvatarProps> = (props) => (
    <Stack vertical justify="center">
        <LoadableAvatar {...props} />
    </Stack>
);

export const CardIconAvatar: React.FC<IconAvatarProps> = (props) => (
    <Stack vertical justify="center">
        <IconAvatar {...props} />
    </Stack>
);
